import React from 'react'
import ReactFlow, { Background, BaseEdge, EdgeLabelRenderer, MarkerType, getSmoothStepPath } from 'reactflow'
import 'reactflow/dist/style.css'
import ELK from 'elkjs/lib/elk.bundled.js'
import { addonLogicToString } from '../model/ScheduleTemplate'

const NODE_STYLE = {
    background: 'var(--bs-secondary-bg, #e9ecef)',
    color: 'var(--bs-body-color, #212529)',
    border: '1px solid var(--bs-secondary)',
}

const NODE_WIDTH = 180
// A few tasks have long enough content (description/condition wrapping)
// to need more room than most - rather than measuring real DOM text
// (elk/dagre don't either, see LAYOUT_RESEARCH.md), just assume every
// box and the gaps around it are 1.5x the original size, uniformly.
const NODE_HEIGHT = 108

// Real data has many unrelated same-level templates (no populated
// dependency) that need wrapping into multiple rows instead of one
// ever-widening row. elk.layered.wrapping.strategy does this natively,
// but it slices the *whole* layered column into side-by-side chunks -
// which can move a deep node above/beside its own dependency, breaking
// the "must be below everything you depend on" guarantee. So wrapping
// is done by hand below, strictly *within* each elk-computed layer -
// it can never move a node across its own layer's boundary.
const MAX_NODES_PER_ROW = 5
const SUB_ROW_GAP = 30
const SIBLING_GAP = 20
const LAYER_GAP = 120

const elk = new ELK()

const ELK_LAYOUT_OPTIONS = {
    'elk.algorithm': 'layered',
    'elk.direction': 'DOWN',
    'elk.edgeRouting': 'ORTHOGONAL',
    'elk.layered.spacing.nodeNodeBetweenLayers': String(LAYER_GAP),
    'elk.spacing.nodeNode': '40',
}

/**
 * A step edge whose bend sits just above the target's layer, instead of
 * at the source/target midpoint. A skip-level dependency (e.g. C->D when
 * B sits between C's and D's layers) would otherwise bend at the
 * midpoint, which lands inside B's row and visually cuts across it -
 * making the diagram look like D depends on B. data.targetLayerStartY is
 * the y of the *start* of the target's layer (before any of its own
 * sub-rows from sibling-wrapping), so the bend always falls within the
 * empty LAYER_GAP band above it, never inside a populated row.
 */
function DependencyEdge({ sourceX, sourceY, targetX, targetY, markerEnd, data }) {
    const centerY = data.targetLayerStartY - LAYER_GAP / 2
    const [path] = getSmoothStepPath({
        sourceX, sourceY, targetX, targetY, centerY, borderRadius: 0,
    })
    return <BaseEdge path={path} markerEnd={markerEnd} />
}

const EDGE_TYPES = { dependency: DependencyEdge }

/**
 * The same 3-segment orthogonal path (vertical, horizontal, vertical)
 * DependencyEdge renders, computed as plain geometry so flow arrows can
 * be placed exactly on it.
 */
function edgeSegments(edge, positionById) {
    const sourcePos = positionById[edge.source]
    const targetPos = positionById[edge.target]
    const sourceX = sourcePos.x + NODE_WIDTH / 2
    const sourceY = sourcePos.y + NODE_HEIGHT
    const targetX = targetPos.x + NODE_WIDTH / 2
    const targetY = targetPos.y
    const centerY = edge.data.targetLayerStartY - LAYER_GAP / 2
    return [
        { x1: sourceX, y1: sourceY, x2: sourceX, y2: centerY, vertical: true },
        { x1: sourceX, y1: centerY, x2: targetX, y2: centerY, vertical: false },
        { x1: targetX, y1: centerY, x2: targetX, y2: targetY, vertical: true },
    ]
}

const ARROW_ROTATION = { down: 90, up: -90, right: 0, left: 180 }
const ARROW_WIDTH = 7
const ARROW_HEIGHT = 9

/**
 * A small arrow at every point that needs disambiguating: each edge's
 * own 90-degree turn (so a bend's continuation is unambiguous), and
 * every point where two *different* edges' lines actually cross (so a
 * crossing doesn't read as a merge/branch). A turn arrow is anchored by
 * its tail (it departs from the corner, showing where the line goes
 * next); a crossing arrow is anchored by its tip (it marks the exact
 * point two unrelated lines cross).
 * @param {[object]} edges
 * @param {object} positionById
 * @return {[{x: number, y: number, rotation: number, anchor: string}]}
 */
function computeFlowArrows(edges, positionById) {
    const arrows = []
    const segmentsByEdge = edges.map(e => edgeSegments(e, positionById))

    edges.forEach((e, i) => {
        const [, seg2, seg3] = segmentsByEdge[i]
        if (seg2.x1 === seg2.x2) return // straight vertical edge, no bend to mark
        arrows.push({ x: seg2.x1, y: seg2.y1, rotation: ARROW_ROTATION[seg2.x2 > seg2.x1 ? 'right' : 'left'], anchor: 'tail' })
        arrows.push({ x: seg3.x1, y: seg3.y1, rotation: ARROW_ROTATION.down, anchor: 'tail' })
    })

    for (let i = 0; i < edges.length; i++) {
        for (let j = i + 1; j < edges.length; j++) {
            const a = edges[i], b = edges[j]
            if ([a.source, a.target].includes(b.source) || [a.source, a.target].includes(b.target)) continue
            segmentsByEdge[i].forEach(segA => {
                segmentsByEdge[j].forEach(segB => {
                    if (segA.vertical === segB.vertical) return
                    const v = segA.vertical ? segA : segB
                    const h = segA.vertical ? segB : segA
                    const vyMin = Math.min(v.y1, v.y2), vyMax = Math.max(v.y1, v.y2)
                    const hxMin = Math.min(h.x1, h.x2), hxMax = Math.max(h.x1, h.x2)
                    if (h.y1 > vyMin && h.y1 < vyMax && v.x1 > hxMin && v.x1 < hxMax) {
                        arrows.push({ x: v.x1, y: h.y1, rotation: ARROW_ROTATION[v.y2 > v.y1 ? 'down' : 'up'], anchor: 'tip' })
                        arrows.push({ x: v.x1, y: h.y1, rotation: ARROW_ROTATION[h.x2 > h.x1 ? 'right' : 'left'], anchor: 'tip' })
                    }
                })
            })
        }
    }
    return arrows
}

/**
 * Runs templates through elkjs's layered algorithm - handles multi-parent
 * merges and multiple independent roots (this is a DAG, not a tree),
 * unlike the previous hand-rolled level/indent computation.
 * @param {[ScheduleTemplate]} templates
 * @param {[object]} addons
 * @return {Promise<{nodes: [object], edges: [object], width: number, height: number}>}
 */
async function layoutTemplateFlowGraph(templates, addons) {
    if (templates.length === 0) return { nodes: [], edges: [], arrows: [], width: 0, height: 0 }

    const idToLocal = {}
    templates.forEach((t, li) => { idToLocal[t.id] = li })

    const elkEdges = []
    templates.forEach(t => {
        (t.dependency || []).forEach(depId => {
            if (idToLocal[depId] === undefined) return
            elkEdges.push({
                id: `e${depId}-${t.id}`,
                sources: [depId],
                targets: [t.id],
            })
        })
    })

    const graph = {
        id: 'root',
        layoutOptions: ELK_LAYOUT_OPTIONS,
        children: templates.map(t => ({
            id: t.id,
            width: NODE_WIDTH,
            height: NODE_HEIGHT,
        })),
        edges: elkEdges,
    }

    const result = await elk.layout(graph)

    // Group by elk's own layer (its y is uniform per layer), then re-flow
    // each layer's nodes - preserving elk's crossing-minimization x order -
    // into rows of at most MAX_NODES_PER_ROW, pushing every later layer
    // down to make room. Layer membership never changes, so a node is
    // always positioned below every layer its dependencies sit in.
    const byLayerY = new Map()
    result.children.forEach(child => {
        const key = Math.round(child.y)
        if (!byLayerY.has(key)) byLayerY.set(key, [])
        byLayerY.get(key).push(child)
    })
    const layerYs = [...byLayerY.keys()].sort((a, b) => a - b)

    const positionById = {}
    let cursorY = 0
    layerYs.forEach(layerY => {
        const layerStartY = cursorY
        const rowNodes = byLayerY.get(layerY).sort((a, b) => a.x - b.x)
        const rowCount = Math.ceil(rowNodes.length / MAX_NODES_PER_ROW)
        rowNodes.forEach((child, i) => {
            const subRow = Math.floor(i / MAX_NODES_PER_ROW)
            const colIndex = i % MAX_NODES_PER_ROW
            positionById[child.id] = {
                x: colIndex * (NODE_WIDTH + SIBLING_GAP),
                y: cursorY + subRow * (NODE_HEIGHT + SUB_ROW_GAP),
                layerStartY,
            }
        })
        cursorY += rowCount * (NODE_HEIGHT + SUB_ROW_GAP) + LAYER_GAP
    })

    const nodes = result.children.map(child => {
        const t = templates[idToLocal[child.id]]
        const condition = addonLogicToString(t.addon_logic, addons)
        const { x, y } = positionById[child.id]
        return {
            id: t.id,
            position: { x, y },
            data: {
                label: (
                    <div style={{ textAlign: 'center', fontSize: '11px' }}>
                        <div style={{ fontWeight: '600' }}>{t.short_description || '(unnamed)'}</div>
                        {t.equip && <div style={{ opacity: 0.85 }}>{t.equip}{t.process ? ' · ' + t.process : ''}</div>}
                        {condition && <div style={{ opacity: 0.75, fontStyle: 'italic' }}><b>IFF</b> {condition}</div>}
                    </div>
                ),
            },
            style: { ...NODE_STYLE, borderRadius: '6px', padding: '6px 10px', width: NODE_WIDTH, minHeight: NODE_HEIGHT },
            sourcePosition: 'bottom',
            targetPosition: 'top',
        }
    })

    const edges = []
    templates.forEach(t => {
        (t.dependency || []).forEach(depId => {
            const depLi = idToLocal[depId]
            if (depLi !== undefined) {
                edges.push({
                    id: `e${depId}-${t.id}`,
                    source: templates[depLi].id,
                    target: t.id,
                    markerEnd: { type: MarkerType.ArrowClosed },
                    type: 'dependency',
                    data: { targetLayerStartY: positionById[t.id].layerStartY },
                })
            }
        })
    })

    const arrows = computeFlowArrows(edges, positionById)

    const maxX = Math.max(...Object.values(positionById).map(p => p.x), 0) + NODE_WIDTH
    return { nodes, edges, arrows, width: maxX, height: cursorY }
}

/**
 * Top-down flow chart of one sign type's schedule templates, laid out by
 * elkjs (async - elk.layout returns a Promise) rather than synchronously.
 * @param {[ScheduleTemplate]} templates
 * @param {[object]} addons
 */
function ScheduleTemplateFlowView({ templates, addons }) {
    const [layout, setLayout] = React.useState(null)

    React.useEffect(() => {
        let cancelled = false
        setLayout(null)
        layoutTemplateFlowGraph(templates, addons).then(result => {
            if (!cancelled) setLayout(result)
        })
        return () => { cancelled = true }
    }, [templates, addons])

    if (templates.length === 0) {
        return <p className="text-muted">No templates to show for this type.</p>
    }

    if (!layout) {
        return <p className="text-muted">Laying out...</p>
    }

    const height = Math.max(layout.height + 80, 180)
    const width = Math.max(layout.width + 40, 320)

    return <div style={{
        height: height + 'px',
        maxHeight: '420px',
        minWidth: width + 'px',
        border: '1px solid var(--bs-border-color)',
        borderRadius: 'var(--bs-border-radius)',
    }}>
        <ReactFlow
            nodes={layout.nodes}
            edges={layout.edges}
            edgeTypes={EDGE_TYPES}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={false}
            proOptions={{ hideAttribution: true }}
        >
            <Background />
            <EdgeLabelRenderer>
                {layout.arrows.map((arrow, i) => {
                    // A CSS border-triangle, not a text glyph, so rotation
                    // can pivot around a specific point *within* the shape
                    // (a text glyph's visual tip drifts off the intended
                    // point once rotated). A crossing arrow is anchored by
                    // its tip (100% 50% - the point at the right edge),
                    // marking the exact point two unrelated lines cross. A
                    // turn arrow is anchored by its tail (0% 50%) instead,
                    // so it departs from the corner showing where the line
                    // goes next, rather than arriving at the corner.
                    const isTip = arrow.anchor === 'tip'
                    return <div key={i} style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        width: 0,
                        height: 0,
                        borderStyle: 'solid',
                        borderWidth: `${ARROW_HEIGHT / 2}px 0 ${ARROW_HEIGHT / 2}px ${ARROW_WIDTH}px`,
                        borderColor: `transparent transparent transparent var(--bs-secondary)`,
                        transformOrigin: isTip ? '100% 50%' : '0% 50%',
                        transform: `translate(${arrow.x - (isTip ? ARROW_WIDTH : 0)}px, ${arrow.y - ARROW_HEIGHT / 2}px) rotate(${arrow.rotation}deg)`,
                        pointerEvents: 'none',
                    }} />
                })}
            </EdgeLabelRenderer>
        </ReactFlow>
    </div>
}

export default ScheduleTemplateFlowView
